// Signage → Alertas y correo (/signage/alertas). Config de alertas de caída de
// pantalla (umbral, destinatarios, recuperación) + SMTP propio del módulo (para
// que el notificador envíe los avisos). Solo lectura de secreto: la clave es
// write-only (se muestra "configurada", nunca el valor).
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Mail, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls } from '@/features/pos/shared';
import { sigList, sigRpc, sigUpdate } from './shared';

interface AlertaCfg { id: string; activa: boolean; umbral_min: number; notificar_recuperacion: boolean; destinatarios: string | null }
interface Smtp { host: string | null; puerto: number | null; seguro: boolean; usuario: string | null; from_email: string | null; from_nombre: string | null; activo: boolean; password_configurada: boolean }

export function AlertasSignage() {
  const qc = useQueryClient();
  const [al, setAl] = useState<AlertaCfg | null>(null);
  const [smtp, setSmtp] = useState<Partial<Smtp & { password: string }>>({});
  const [gA, setGA] = useState(false); const [gS, setGS] = useState(false);

  const { data: alRows } = useQuery<AlertaCfg[]>({ queryKey: ['sig-alerta-cfg'], queryFn: () => sigList<AlertaCfg>('alerta_config?select=*&deleted_at=is.null&limit=1') });
  const { data: smtpData } = useQuery<Smtp | null>({ queryKey: ['sig-smtp'], queryFn: () => sigRpc<Smtp | null>('sig_smtp_get') });

  useEffect(() => { if (alRows?.[0]) setAl(alRows[0]); }, [alRows]);
  useEffect(() => { if (smtpData) setSmtp({ ...smtpData, password: '' }); }, [smtpData]);

  const guardarAlertas = async () => {
    if (!al) return;
    setGA(true);
    try {
      await sigUpdate('alerta_config', al.id, {
        activa: al.activa, umbral_min: Number(al.umbral_min) || 5,
        notificar_recuperacion: al.notificar_recuperacion, destinatarios: al.destinatarios || null,
      });
      void qc.invalidateQueries({ queryKey: ['sig-alerta-cfg'] });
      toast.success('Alertas guardadas');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGA(false); }
  };

  const guardarSmtp = async () => {
    setGS(true);
    try {
      await sigRpc('sig_smtp_set', { _data: {
        host: smtp.host || null, puerto: Number(smtp.puerto) || 587, seguro: smtp.seguro ?? false,
        usuario: smtp.usuario || null, password: smtp.password || '', from_email: smtp.from_email || null,
        from_nombre: smtp.from_nombre || null, activo: smtp.activo ?? false,
      } });
      setSmtp((s) => ({ ...s, password: '' }));
      void qc.invalidateQueries({ queryKey: ['sig-smtp'] });
      toast.success('SMTP guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGS(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><BellRing className="w-5 h-5" /> Alertas y correo</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Alertas de pantalla caída</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {al ? (
            <>
              <Switch checked={al.activa} onChange={(v) => setAl({ ...al, activa: v })} label="Avisar cuando una pantalla deja de reportar" />
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Umbral (minutos sin señal)">
                  <input className={`${fieldCls} !w-28 text-right`} type="number" min={1} max={240} value={al.umbral_min}
                         onChange={(e) => setAl({ ...al, umbral_min: Number(e.target.value) })} />
                </Campo>
                <div className="self-end pb-1"><Switch checked={al.notificar_recuperacion} onChange={(v) => setAl({ ...al, notificar_recuperacion: v })} label="Avisar también al reconectarse" /></div>
              </div>
              <Campo label="Destinatarios (correos separados por coma)">
                <input className={fieldCls} placeholder="ti@empresa.com, soporte@empresa.com" value={al.destinatarios ?? ''}
                       onChange={(e) => setAl({ ...al, destinatarios: e.target.value })} />
              </Campo>
              <p className="text-xs text-muted-foreground">
                El aviso se envía por correo (configura el SMTP abajo) cuando una pantalla activa no reporta en el umbral. Solo se avisa
                en el cambio de estado (sin spam) y se agrupan las pantallas del mismo aviso. Sin destinatarios, no se envía nada.
              </p>
              <div className="flex justify-end"><Button onClick={() => void guardarAlertas()} isLoading={gA}><Save className="w-4 h-4 mr-1" /> Guardar alertas</Button></div>
            </>
          ) : <div className="text-sm text-muted-foreground">Cargando…</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Correo del módulo (SMTP)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Switch checked={smtp.activo ?? false} onChange={(v) => setSmtp({ ...smtp, activo: v })} label="SMTP activo (envía los avisos)" />
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Host"><input className={fieldCls} placeholder="smtp.gmail.com" value={smtp.host ?? ''} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} /></Campo>
            <Campo label="Puerto"><input className={`${fieldCls} !w-28`} type="number" value={smtp.puerto ?? 587} onChange={(e) => setSmtp({ ...smtp, puerto: Number(e.target.value) })} /></Campo>
            <Campo label="Usuario"><input className={fieldCls} value={smtp.usuario ?? ''} onChange={(e) => setSmtp({ ...smtp, usuario: e.target.value })} /></Campo>
            <Campo label="Contraseña">
              <input className={fieldCls} type="password" autoComplete="new-password"
                     placeholder={smtp.password_configurada ? '•••••••• (configurada)' : ''}
                     value={smtp.password ?? ''} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} />
            </Campo>
            <Campo label="From (correo)"><input className={fieldCls} placeholder="cartelera@empresa.com" value={smtp.from_email ?? ''} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} /></Campo>
            <Campo label="From (nombre)"><input className={fieldCls} placeholder="Cartelería" value={smtp.from_nombre ?? ''} onChange={(e) => setSmtp({ ...smtp, from_nombre: e.target.value })} /></Campo>
          </div>
          <Switch checked={smtp.seguro ?? false} onChange={(v) => setSmtp({ ...smtp, seguro: v })} label="Conexión segura directa (TLS/465). Apagado = STARTTLS (587)" />
          <p className="text-xs text-muted-foreground">Deja la contraseña en blanco para conservar la actual. Cada módulo usa su propio SMTP.</p>
          <div className="flex justify-end"><Button onClick={() => void guardarSmtp()} isLoading={gS}><Save className="w-4 h-4 mr-1" /> Guardar SMTP</Button></div>
        </CardContent>
      </Card>
    </div>
  );
}
